import "./style/anchored-tooltip.scss"

import useOnClickOutside from "@ng-mw/reol/utils/useOnClickOutside"
import classNames from "classnames"
import React, {
    CSSProperties,
    ReactElement,
    RefObject,
    useCallback,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
    type ReactNode,
} from "react"

export type AnchoredTooltipPosition = "left" | "center" | "right"

type TooltipLayout = {
    arrowLeft: number
    bubbleLeft: number
    bubbleWidth: number
}

const VIEWPORT_PADDING = 16
const MIN_ARROW_INSET = 26
const PREFERRED_ARROW_INSET = 30
const MIN_BUBBLE_WIDTH = 240
const MAX_BUBBLE_WIDTH = 360
const VIEWPORT_WIDTH_RATIO = 2 / 3

const DEFAULT_LAYOUT: TooltipLayout = {
    arrowLeft: 28,
    bubbleLeft: -220,
    bubbleWidth: 320,
}

const clamp = (value: number, min: number, max: number): number =>
    Math.max(min, Math.min(value, max))

const getTooltipWidth = (viewportWidth: number): number =>
    Math.min(
        MAX_BUBBLE_WIDTH,
        Math.max(MIN_BUBBLE_WIDTH, Math.floor(viewportWidth * VIEWPORT_WIDTH_RATIO)),
        Math.max(0, viewportWidth - VIEWPORT_PADDING * 2),
    )

const getAnchorHorizontalCenter = (
    anchorRef: AnchoredTooltipProps["anchorRef"],
    leftCorrection: number,
    offsetLeft: string,
): number =>
    anchorRef?.current
        ? anchorRef.current.offsetLeft + leftCorrection + anchorRef.current.offsetWidth * 0.5
        : Number.parseFloat(offsetLeft)

const getPreferredBubbleLeft = (position: AnchoredTooltipPosition, bubbleWidth: number): number => {
    if (position === "left") {
        return -PREFERRED_ARROW_INSET
    }

    if (position === "center") {
        return bubbleWidth * -0.5
    }

    return -(bubbleWidth - PREFERRED_ARROW_INSET)
}

const getTooltipLayout = (
    anchorHorizontalCenter: number,
    position: AnchoredTooltipPosition,
    viewportWidth: number,
): TooltipLayout => {
    const bubbleWidth = getTooltipWidth(viewportWidth)
    const preferredBubbleLeft = getPreferredBubbleLeft(position, bubbleWidth)
    const minBubbleLeft = VIEWPORT_PADDING - anchorHorizontalCenter
    const maxBubbleLeft = viewportWidth - VIEWPORT_PADDING - bubbleWidth - anchorHorizontalCenter
    const bubbleLeft = clamp(preferredBubbleLeft, minBubbleLeft, maxBubbleLeft)
    const arrowLeft = clamp(-bubbleLeft, MIN_ARROW_INSET, bubbleWidth - MIN_ARROW_INSET)

    return {
        arrowLeft,
        bubbleLeft,
        bubbleWidth,
    }
}

const getAnchorWrapperStyle = (
    anchorRef: AnchoredTooltipProps["anchorRef"],
    leftCorrection: number,
    offsetLeft: string,
    offsetTop: string,
    topCorrection: number,
): CSSProperties =>
    anchorRef?.current
        ? {
              position: "absolute",
              left: `${
                  anchorRef.current.offsetLeft +
                  leftCorrection +
                  anchorRef.current.offsetWidth * 0.5
              }px`,
              top: `${anchorRef.current.offsetTop + topCorrection}px`,
              width: "0",
              height: "0",
          }
        : {
              position: "absolute",
              left: offsetLeft,
              top: offsetTop,
              width: "0",
              height: "0",
          }

interface AnchoredTooltipProps {
    anchorRef?: RefObject<HTMLElement | null>
    body?: ReactNode
    className?: string
    isDismissedOnAnchorClick?: boolean
    isCloseButtonVisible?: boolean
    isDismissedOnOutsideClick?: boolean
    leftCorrection?: number
    offsetLeft?: string
    offsetTop?: string
    onClose?: () => void
    position?: AnchoredTooltipPosition
    topCorrection?: number
}

const AnchoredTooltip = ({
    anchorRef,
    body = null,
    className,
    isDismissedOnAnchorClick = false,
    isCloseButtonVisible = false,
    isDismissedOnOutsideClick = true,
    leftCorrection = 0,
    offsetLeft = "0",
    offsetTop = "0",
    onClose = () => undefined,
    position = "right",
    topCorrection = 0,
}: AnchoredTooltipProps): ReactElement | null => {
    const tooltipRef = useRef<HTMLDivElement>(null)
    const uniqueId = useId()
    const [layout, setLayout] = useState<TooltipLayout>(DEFAULT_LAYOUT)

    const hideTooltip = useCallback(() => {
        onClose()
    }, [onClose])

    useOnClickOutside(
        tooltipRef,
        () => {
            if (!isDismissedOnOutsideClick) {
                return
            }
            hideTooltip()
        },
        undefined,
    )

    useEffect(() => {
        if (!isDismissedOnAnchorClick || !anchorRef?.current) {
            return
        }

        const anchorElement = anchorRef.current
        const handleAnchorClick = () => {
            hideTooltip()
        }

        anchorElement.addEventListener("click", handleAnchorClick)

        return () => {
            anchorElement.removeEventListener("click", handleAnchorClick)
        }
    }, [anchorRef, hideTooltip, isDismissedOnAnchorClick])

    useLayoutEffect(() => {
        if (typeof window === "undefined") {
            return
        }

        const updateLayout = () => {
            const anchorHorizontalCenter = getAnchorHorizontalCenter(
                anchorRef,
                leftCorrection,
                offsetLeft,
            )
            setLayout(getTooltipLayout(anchorHorizontalCenter, position, window.innerWidth))
        }

        updateLayout()
        window.addEventListener("resize", updateLayout)

        return () => {
            window.removeEventListener("resize", updateLayout)
        }
    }, [anchorRef, leftCorrection, offsetLeft, position])

    const wrapperStyle = getAnchorWrapperStyle(
        anchorRef,
        leftCorrection,
        offsetLeft,
        offsetTop,
        topCorrection,
    )

    return (
        <div style={wrapperStyle}>
            <div
                className={classNames("anchored-tooltip", "anchored-tooltip--visible", className)}
                id={uniqueId}
                ref={tooltipRef}
                role="tooltip"
                style={{
                    "--anchored-tooltip-arrow-left": `${layout.arrowLeft}px`,
                    "--anchored-tooltip-bubble-left": `${layout.bubbleLeft}px`,
                    "--anchored-tooltip-bubble-width": `${layout.bubbleWidth}px`,
                }}
            >
                <div className="anchored-tooltip__body">{body}</div>
                {isCloseButtonVisible ? (
                    <button
                        aria-label="Lukk tooltip"
                        className="anchored-tooltip__close"
                        onClick={hideTooltip}
                        type="button"
                    />
                ) : null}
            </div>
        </div>
    )
}

export default AnchoredTooltip
